import { useState, useEffect } from 'react'
import { 
  Container, 
  useToast,
  VStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Box,
  Image,
  Icon
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { FiImage, FiVideo, FiFileText, FiFile } from 'react-icons/fi'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import DataTable from '../../common/DataTable/DataTable'
import apiClient from '../../../utils/apiClient'

const Media = () => {
  const [media, setMedia] = useState([])
  const [filteredMedia, setFilteredMedia] = useState([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return FiImage;
    if (fileType.startsWith('video/')) return FiVideo;
    if (fileType === 'application/pdf') return FiFileText;
    return FiFile;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Enhanced columns with better formatting
  const columns = [
    { 
      key: 'file_name', 
      label: 'File Name',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium" noOfLines={1}>{value}</Text>
          <Text fontSize="xs" color="gray.500">ID: #{row.id}</Text>
        </VStack>
      )
    },
    { 
      key: 'file_type', 
      label: 'Type',
      render: (value) => {
        const FileIcon = getFileIcon(value);
        return (
          <VStack align="start" spacing={1}>
            <Badge colorScheme="blue" variant="subtle">
              <Icon as={FileIcon} mr={1} />
              {value}
            </Badge>
          </VStack>
        )
      }
    },
    { 
      key: 'file_size', 
      label: 'Size',
      render: (value) => (
        <Text fontSize="sm">
          {formatFileSize(value || 0)}
        </Text>
      )
    },
    { 
      key: 'file_path', 
      label: 'Preview',
      render: (value, row) => {
        if (row.file_type && row.file_type.startsWith('image/')) {
          return (
            <Image
              src={`${process.env.REACT_APP_API_BASE_URL}${value}`}
              alt={row.file_name}
              boxSize="40px"
              objectFit="cover"
              borderRadius="md"
              fallback={<Icon as={FiImage} boxSize={6} color="gray.400" />}
            />
          );
        }
        return (
          <Icon as={getFileIcon(row.file_type)} boxSize={6} color="gray.400" />
        );
      }
    },
    { 
      key: 'updated_at', 
      label: 'Uploaded',
      render: (value) => (
        <Text fontSize="sm" color="gray.600">
          {value ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'N/A'}
        </Text>
      )
    }
  ]

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  const fetchAssets = async () => {
    try {
      const data = await apiClient(`/assets/${tenantId}`)
      setMedia(data)
      setFilteredMedia(data)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch media assets',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssets()
  }, [])

  const handleSearch = (searchTerm) => {
    const lowercaseSearch = searchTerm.toLowerCase()
    const filtered = media.filter(item =>
      item.file_name.toLowerCase().includes(lowercaseSearch) ||
      item.file_type.toLowerCase().includes(lowercaseSearch)
    )
    setFilteredMedia(filtered)
  }

  const handleEdit = (media) => {
    navigate(`/media/edit/${media.id}`)
  }

  const handleCreate = () => {
    navigate(`/media/create`)
  }
 
  const handleDelete = async (model) => {
    try {
      const response = await apiClient(`/assets/${tenantId}/${model.id}`, {
        method: 'DELETE'
      })

      toast({
        title: 'Success',
        description: 'Media asset deleted successfully',
        status: 'success',
        duration: 3000
      })

      fetchAssets()
      return response.data
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete media asset',
        status: 'error',
        duration: 3000
      })
    }
  }

  // Calculate statistics
  const getMediaStats = () => {
    const total = media.length
    const images = media.filter(item => item.file_type && item.file_type.startsWith('image/')).length
    const videos = media.filter(item => item.file_type && item.file_type.startsWith('video/')).length
    const documents = media.filter(item => 
      item.file_type && 
      (item.file_type === 'application/pdf' || 
       item.file_type.includes('document') || 
       item.file_type.includes('text/'))
    ).length
    const totalSize = media.reduce((acc, item) => acc + (item.file_size || 0), 0)
    
    return { total, images, videos, documents, totalSize }
  }

  const stats = getMediaStats()

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Media</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>Media Library</Heading>
          <Text color="gray.500">
            Manage your media assets. Upload, organize, and use images, videos, and documents.
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Files</StatLabel>
                <StatNumber color="blue.500">{stats.total}</StatNumber>
                <StatHelpText>{formatFileSize(stats.totalSize)}</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Images</StatLabel>
                <StatNumber color="green.500">{stats.images}</StatNumber>
                <StatHelpText>Photos & graphics</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Videos</StatLabel>
                <StatNumber color="purple.500">{stats.videos}</StatNumber>
                <StatHelpText>Video files</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Documents</StatLabel>
                <StatNumber color="orange.500">{stats.documents}</StatNumber>
                <StatHelpText>PDFs & docs</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredMedia}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search media by filename or type..."
              isLoading={loading}
              title="Media Management"
              createButtonText="Upload Media"
              emptyStateTitle="No media files found"
              emptyStateDescription={
                filteredMedia.length === 0 && media.length > 0
                  ? "No media files match your search criteria. Try adjusting your search terms."
                  : "Get started by uploading your first media file to build your library."
              }
              loadingText="Loading media..."
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default Media
